// Companies.js — Browse companies and apply with resume upload
import React, { useEffect, useState, useCallback, useRef } from 'react';
import Navbar from './Navbar';
import API from '../api';

const FILTERS = ['All', 'Eligible', 'Not Eligible', 'Applied'];

// Resume Upload Modal
function ResumeModal({ company, onClose, onSubmit, loading }) {
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(ext)) {
      setError('Only PDF, DOC, DOCX files are allowed.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError('File size must be under 5MB.');
      return;
    }
    setError('');
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = () => {
    if (!file) { setError('Please select a resume file.'); return; }
    onSubmit(file);
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Apply to {company.company}</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: '0.25rem' }}>Upload your resume to submit the application</p>
          </div>
          <button onClick={onClose} style={closeBtn}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Company info strip */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <span style={infoPill}>Min CGPA: {company.min_cgpa}</span>
          <span style={infoPill}>Max Arrears: {company.max_arrears}</span>
          <span style={infoPill}>{company.department}</span>
        </div>

        {/* Drop zone */}
        <div
          style={{ ...dropZone, borderColor: drag ? 'var(--accent-blue)' : file ? 'var(--accent-green)' : 'var(--border)', background: drag ? 'rgba(79,121,246,0.07)' : file ? 'rgba(46,204,113,0.05)' : 'rgba(255,255,255,0.03)' }}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current.click()}
        >
          <input ref={inputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
          {file ? (
            <>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '0.5rem' }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              <p style={{ fontWeight: 600, color: 'var(--accent-green)', margin: 0 }}>{file.name}</p>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>{(file.size / 1024).toFixed(1)} KB — Click to change</p>
            </>
          ) : (
            <>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '0.5rem' }}>
                <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
              </svg>
              <p style={{ fontWeight: 600, margin: 0 }}>Drag & drop or click to upload</p>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>PDF, DOC, DOCX — Max 5MB</p>
            </>
          )}
        </div>

        {error && (
          <div style={{ color: 'var(--accent-red)', fontSize: '0.8rem', marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
          <button className="btn btn-outline" style={{ flex: 1 }} onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 2 }} onClick={handleSubmit} disabled={loading || !file}>
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                Submitting...
              </span>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                Submit Application
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal styles
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: '1rem', backdropFilter: 'blur(4px)'
};
const modal = {
  background: 'var(--bg-secondary)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius)', padding: '1.75rem',
  width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
};
const closeBtn = {
  background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
  borderRadius: '8px', padding: '0.4rem', cursor: 'pointer',
  color: 'var(--text-muted)', display: 'flex', alignItems: 'center'
};
const infoPill = {
  background: 'rgba(79,121,246,0.1)', border: '1px solid rgba(79,121,246,0.2)',
  color: 'var(--accent-blue)', borderRadius: '50px',
  fontSize: '0.75rem', fontWeight: 600, padding: '0.2rem 0.7rem'
};
const dropZone = {
  border: '2px dashed', borderRadius: 'var(--radius-sm)',
  padding: '2rem 1rem', textAlign: 'center', cursor: 'pointer',
  transition: 'all 0.2s ease', display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center'
};

// ─── Main Component ───────────────────────────────────────────
export default function Companies() {
  const student = JSON.parse(localStorage.getItem('student')) || {};

  const [companies,    setCompanies]   = useState([]);
  const [applied,      setApplied]     = useState(new Set());
  const [activeFilter, setFilter]      = useState('All');
  const [loading,      setLoading]     = useState(true);
  const [applyStatus,  setApplyStatus] = useState({});
  const [search,       setSearch]      = useState('');
  const [modalCompany, setModalCompany] = useState(null); // company to apply to
  const [modalLoading, setModalLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!student.id) return;
    setLoading(true);
    try {
      const [eligRes, appRes] = await Promise.all([
        API.get(`/eligible_companies/${student.id}`),
        API.get(`/applications/${student.id}`),
      ]);
      setCompanies(eligRes.data);
      setApplied(new Set(appRes.data.map(a => a.company)));
    } catch (err) {
      console.error('Failed to load companies:', err);
    } finally {
      setLoading(false);
    }
  }, [student.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApply = async (file) => {
    if (!modalCompany) return;
    setModalLoading(true);
    const formData = new FormData();
    formData.append('student_id', student.id);
    formData.append('company_id', modalCompany.id);
    formData.append('resume', file);
    try {
      await API.post('/apply', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setApplied(prev => new Set([...prev, modalCompany.company]));
      setApplyStatus(prev => ({ ...prev, [modalCompany.id]: 'success' }));
      setModalCompany(null);
    } catch (err) {
      const msg = err.response?.data?.error || 'Application failed';
      setApplyStatus(prev => ({ ...prev, [modalCompany.id]: msg }));
      setModalCompany(null);
      setTimeout(() => setApplyStatus(prev => { const n = { ...prev }; delete n[modalCompany.id]; return n; }), 4000);
    } finally {
      setModalLoading(false);
    }
  };

  const filtered = companies.filter(c => {
    const matchSearch = c.company.toLowerCase().includes(search.toLowerCase()) ||
                        c.department.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (activeFilter === 'Eligible')     return c.eligible;
    if (activeFilter === 'Not Eligible') return !c.eligible;
    if (activeFilter === 'Applied')      return applied.has(c.company);
    return true;
  });

  const eligibleCount = companies.filter(c => c.eligible).length;

  return (
    <>
      <Navbar />
      {modalCompany && (
        <ResumeModal
          company={modalCompany}
          loading={modalLoading}
          onClose={() => setModalCompany(null)}
          onSubmit={handleApply}
        />
      )}

      <div className="container" style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>
        <div className="page-header">
          <h2>Companies</h2>
          <p>{loading ? 'Loading companies...' : `${companies.length} companies · ${eligibleCount} eligible for you`}</p>
        </div>

        {/* Search + Filters */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              style={{ width: '100%', padding: '0.6rem 1rem 0.6rem 2.25rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', outline: 'none' }}
              placeholder="Search company or department..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-bar" style={{ margin: 0 }}>
            {FILTERS.map(f => (
              <button key={f} className={`filter-btn ${activeFilter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
                {f}
                {f === 'Eligible' && !loading && <span className="nav-badge" style={{ marginLeft: 6, background: 'var(--accent-green)' }}>{eligibleCount}</span>}
                {f === 'Applied'  && !loading && applied.size > 0 && <span className="nav-badge" style={{ marginLeft: 6 }}>{applied.size}</span>}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="loader-overlay"><div className="spinner" /></div>}

        {!loading && filtered.length === 0 && (
          <div className="empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: '1rem' }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <h3>No companies found</h3>
            <p>Try changing the filter or search term.</p>
          </div>
        )}

        {!loading && (
          <div className="companies-grid">
            {filtered.map(company => {
              const isApplied = applied.has(company.company);
              const status    = applyStatus[company.id];

              return (
                <div key={company.id} className="card company-card"
                  style={{ borderColor: company.eligible ? 'rgba(46,204,113,0.2)' : isApplied ? 'rgba(79,121,246,0.2)' : 'var(--border)' }}
                >
                  <div className="company-card-top">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <div className="company-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: company.eligible ? 'var(--accent-green)' : 'var(--accent-red)', flexShrink: 0, display: 'inline-block' }} />
                        {company.company}
                      </div>
                      {isApplied && <span className="badge badge-blue">Applied</span>}
                    </div>

                    <div className="company-meta">
                      {[
                        { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>, label: 'Min CGPA', value: company.min_cgpa, ok: student.cgpa >= company.min_cgpa, yours: student.cgpa },
                        { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>, label: 'Max Arrears', value: company.max_arrears, ok: student.arrears <= company.max_arrears, yours: student.arrears },
                        { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>, label: 'Department', value: company.department, ok: student.department === company.department, yours: student.department },
                      ].map((item, i) => (
                        <div key={i} className="company-meta-item">
                          <span style={{ color: 'var(--text-muted)' }}>{item.icon}</span>
                          <span>{item.label}: <strong>{item.value}</strong></span>
                          <span style={{ color: item.ok ? 'var(--accent-green)' : 'var(--accent-red)', fontSize: '0.7rem' }}>
                            {item.ok ? '✓' : '✗'} {item.yours}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="company-card-bottom" style={{ marginTop: '1rem' }}>
                    {company.eligible
                      ? <span className="badge badge-green">Eligible</span>
                      : <span className="badge badge-red">Not Eligible</span>}

                    {company.eligible && !isApplied && status !== 'success' && (
                      <button className="btn btn-primary btn-sm" onClick={() => setModalCompany(company)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                        Apply
                      </button>
                    )}
                    {(isApplied || status === 'success') && (
                      <span style={{ fontSize: '0.8rem', color: 'var(--accent-blue)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                        Applied
                      </span>
                    )}
                    {status && status !== 'loading' && status !== 'success' && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--accent-red)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        {status}
                      </span>
                    )}
                    {!company.eligible && !isApplied && (
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Not Eligible</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
